import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
  Paper,
  Fade,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { getUser, updateUser, updateUserPwd } from '../services/api_service';
import { useAlert } from '../AlertContext';

const ModernProfile = () => {
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editable, setEditable] = useState(false);
  const [passwordEditable, setPasswordEditable] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    password_confirmation: '',
  });
  const { showAlert } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const session = JSON.parse(localStorage.getItem('session') || '{}');

  useEffect(() => {
    getUserData();
  }, []);

  const getUserData = async () => {
    try {
      setLoading(true);
      const response = await getUser(session?.user_id);
      if (response?.result) {
        setUser(response.result);
      }
    } catch (error) {
      showAlert('Error al cargar datos del usuario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onChange = (key, value) => {
    setUser((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const onPasswordChange = (key, value) => {
    setPasswordData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const response = await updateUser(user.id, user);
      if (response?.result) {
        //setUser(response.result);
        setEditable(false);
        showAlert('Datos actualizados correctamente', 'success');
      }
    } catch (error) {
      showAlert('Error al actualizar datos', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.password.length < 6) {
      showAlert('La contraseña debe tener al menos 6 caracteres', 'warning');
      return;
    }
    if (passwordData.password !== passwordData.password_confirmation) {
      showAlert('Las contraseñas no coinciden', 'warning');
      return;
    }

    setUpdating(true);
    try {
      await updateUserPwd(user.id, { password: passwordData.password });
      setPasswordEditable(false);
      setPasswordData({ password: '', password_confirmation: '' });
      showAlert('Contraseña actualizada correctamente', 'success');
    } catch (error) {
      showAlert('Error al actualizar contraseña', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditable(false);
    setPasswordEditable(false);
    setPasswordData({ password: '', password_confirmation: '' });
    getUserData();
  };

  const stringToColor = (string) => {
    if (!string) return '#94B754';
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    return names.length >= 2
      ? `${names[0][0]}${names[1][0]}`.toUpperCase()
      : names[0][0].toUpperCase();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#94B754' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F8FFFE 0%, #FFFFFF 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Fade in={true} timeout={600}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid #E0E0E0',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                color: 'white',
                p: { xs: 3, md: 4 },
                textAlign: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 80, md: 100 },
                  height: { xs: 80, md: 100 },
                  mx: 'auto',
                  mb: 2,
                  backgroundColor: stringToColor(user.name),
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 'bold',
                  border: '4px solid rgba(255,255,255,0.2)',
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                {user.name?.toUpperCase() || 'USUARIO'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {user.identification_type} {user.identification_number}
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={3}>
                {/* Personal Information Section */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: '#2C5530',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <PersonIcon sx={{ mr: 1 }} />
                    {passwordEditable
                      ? 'Cambiar Contraseña'
                      : 'Información Personal'}
                  </Typography>

                  <Stack spacing={2}>
                    {!passwordEditable ? (
                      <>
                        <TextField
                          label="Nombre Completo"
                          value={user.name || ''}
                          onChange={(e) => onChange('name', e.target.value)}
                          disabled={!editable || updating}
                          helperText="Nombre completo tal como aparece en tu documento"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        <TextField
                          label="Correo Electrónico"
                          value={user.email || ''}
                          onChange={(e) => onChange('email', e.target.value)}
                          disabled={!editable || updating}
                          type="email"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        <TextField
                          label="Dirección"
                          value={user.address || ''}
                          onChange={(e) => onChange('address', e.target.value)}
                          disabled={!editable || updating}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        <TextField
                          label="Teléfono"
                          value={user.phone || ''}
                          onChange={(e) => onChange('phone', e.target.value)}
                          disabled={!editable || updating}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <TextField
                          label="Nueva Contraseña"
                          type="password"
                          value={passwordData.password}
                          onChange={(e) =>
                            onPasswordChange('password', e.target.value)
                          }
                          disabled={updating}
                          helperText="Mínimo 6 caracteres"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        <TextField
                          label="Confirmar Contraseña"
                          type="password"
                          value={passwordData.password_confirmation}
                          onChange={(e) =>
                            onPasswordChange(
                              'password_confirmation',
                              e.target.value
                            )
                          }
                          disabled={updating}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Action Buttons */}
                <Box>
                  {editable || passwordEditable ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      justifyContent="center"
                    >
                      <Button
                        variant="contained"
                        size="large"
                        onClick={editable ? handleUpdate : handlePasswordUpdate}
                        disabled={updating}
                        startIcon={
                          updating ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{
                          borderRadius: 2,
                          background:
                            'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          minWidth: 140,
                        }}
                      >
                        {updating ? 'Guardando...' : 'Guardar'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleCancel}
                        disabled={updating}
                        startIcon={<CancelIcon />}
                        sx={{
                          borderRadius: 2,
                          borderColor: '#94B754',
                          color: '#94B754',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          minWidth: 140,
                          '&:hover': {
                            borderColor: '#7DA647',
                            backgroundColor: 'rgba(148, 183, 84, 0.1)',
                          },
                        }}
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  ) : (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      justifyContent="center"
                    >
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => setEditable(true)}
                        startIcon={<EditIcon />}
                        sx={{
                          borderRadius: 2,
                          background:
                            'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          minWidth: 180,
                        }}
                      >
                        Actualizar Datos
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setPasswordEditable(true)}
                        startIcon={<LockIcon />}
                        sx={{
                          borderRadius: 2,
                          borderColor: '#94B754',
                          color: '#94B754',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          minWidth: 180,
                          '&:hover': {
                            borderColor: '#7DA647',
                            backgroundColor: 'rgba(148, 183, 84, 0.1)',
                          },
                        }}
                      >
                        Cambiar Contraseña
                      </Button>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default ModernProfile;
